import React, { Component, Fragment } from 'react'
import { SpriteSheet3D } from 'aria-viewer/interactive-element/components/3d/spriteSheet3D/'
import { HitCircle3D } from 'aria-viewer/interactive-element/components/3d/hitMesh3D/'

import HOTSPOT_BODY from 'public/assets/png/spriteAtlases/hotspot_body_2.png'

import HOTSPOT_ICONS from 'public/assets/png/spriteAtlases/hotspots_icons.png'
import HOTSPOT_TEXT_ATLAS from 'public/assets/png/spriteAtlases/hotspot_text_atlas.png'
import VISITED_CHECK from 'public/assets/png/spriteAtlases/visited_check_large.png'
import { dispose3DObject, standardizeScale, disposeAnimations } from '../../../utils'
import { isFullScreenActive } from 'aria-viewer/utils/fullScreen'

import { MEDIA_TYPES } from 'aria-viewer/tour/constants'
import { Breakpoints } from 'aria-viewer/interactive-element/constants'

import {
    Hotspot3DAttributeValues,
    Hotspot3DSpeeds,
} from 'aria-viewer/interactive-element/components/3d/hotspot3D/constants/'

import { Capabilities, displayFeatureBasedOnViewportSize } from 'aria-viewer/utils/capabilities'
import getBool from '../../../../utils/getBool'
import { getEnv } from '../../../../utils/environment'

// Wrapper component that defines callbacks to manipulate attributes on callbacks to drive animations
// on wrapped 3d mesh component

const enableGaze = getBool(getEnv('REACT_APP_ENABLE_GAZE_LISTENER'))

class Hotspot3D extends Component {
    constructor(props) {
        super(props)
        this.state = {
            currentVideoState: props.autoplay ? 'playing' : 'paused',
            rootObj: null,
        }
        this.actions = {
            icon: {},
            bodyBorder: {},
            bodyBlur: {},
        }
        this.clips = {}
        this.mixers = {}
        this.clock = new THREE.Clock()
        this.raf
        this.rafMesh
        this.iconObj
        this.bodyBlurObj
        this.bodyBorderObj
        this.allowAnimations = true
    }

    componentDidMount() {
        const {
            scene,
            meshPosition,
            rootObj,
            camera,
            renderer,
            height,
            viewportWidth,
            absoluteScale,
            useResponsiveUI,
        } = this.props

        let _rootObj = new THREE.Object3D()
        _rootObj.name = 'hotspotRoot'
        _rootObj.position.set(meshPosition.x, meshPosition.y, meshPosition.z)
        this.setState({ rootObj: _rootObj })
        let targetScale = useResponsiveUI ? absoluteScale * (viewportWidth / 1200) : absoluteScale
        _rootObj = standardizeScale(_rootObj, camera, renderer, height, targetScale)
        // Copying quaternion minimizes distortion at the edges of visible fov
        // Tradeoff is introduction of a slight parallax between composed sprites
        //  Alternative is to just have obj look to origin at initialization
        // Quaternion needs to be copied in requestAnimationFrame call (See below)
        // in order to maintain orientation, so there is additional performance hit
        // NOTE: May want to paramterize these two rotational approaches so either can be selected
        _rootObj.quaternion.copy(camera.quaternion)
        // Alternative:
        // _rootObj.lookAt(0, 0, 0)

        if (rootObj) {
            rootObj.add(_rootObj)
        } else {
            scene.add(_rootObj)
        }

        // Gives webgl children time to mount
        this.createAnimations = setTimeout(() => {
            _rootObj && this.createWebGlAnimations(_rootObj)
        }, 1000)

        this.animateHotspot()
    }

    componentDidUpdate(prevProps) {
        const { currentState } = this.props

        // Toggle between panning and default animation based on panning state
        // Use of currentlyPanning boolean prevents throttling of animation on update
        if (currentState === 'panning' && prevProps.currentState !== 'panning') {
            this.setPanningAnimation()
        } else if (currentState === 'default' && prevProps.currentState !== 'default') {
            this.setDefaultAnimation()
        } else if (currentState === 'visited' && prevProps.currentState !== 'visited') {
            this.setVisitedState()
        }
    }

    setVisitedState = () => {
        // TO DO: Abstract this model for crawling play and stop for every use case
        this.actions.bodyBorder.defaultScaleClip && this.actions.bodyBorder.defaultScaleClip.stop()
        this.actions.bodyBlur.defaultScaleClip && this.actions.bodyBlur.defaultScaleClip.stop()

        this.actions.bodyBorder.defaultOpacityClip && this.actions.bodyBorder.defaultOpacityClip.stop()
        this.actions.bodyBlur.defaultOpacityClip && this.actions.bodyBlur.defaultOpacityClip.stop()

        this.actions.icon.defaultOpacityClip && this.actions.icon.defaultOpacityClip.stop()

        Object.keys(this.actions).forEach(key => {
            this.actions[key] && this.actions[key].visitedClip && this.actions[key].visitedClip.play()
        })
    }

    setDefaultAnimation() {
        const { timeoutRandomizerScale } = this.props
        this.actions.icon.panningScaleClip && this.actions.icon.panningScaleClip.stop()

        this.defaultAnimations = setTimeout(() => {
            this.actions.bodyBorder.defaultScaleClip && this.actions.bodyBorder.defaultScaleClip.play()
            this.actions.bodyBlur.defaultScaleClip && this.actions.bodyBlur.defaultScaleClip.play()

            this.actions.bodyBorder.defaultOpacityClip && this.actions.bodyBorder.defaultOpacityClip.play()
            this.actions.bodyBlur.defaultOpacityClip && this.actions.bodyBlur.defaultOpacityClip.play()

            this.actions.icon.defaultOpacityClip && this.actions.icon.defaultOpacityClip.play()
        }, Math.random() * timeoutRandomizerScale)
    }

    setPanningAnimation() {
        this.actions.bodyBorder.defaultScaleClip && this.actions.bodyBorder.defaultScaleClip.stop()
        this.actions.bodyBlur.defaultScaleClip && this.actions.bodyBlur.defaultScaleClip.stop()

        this.actions.bodyBorder.defaultOpacityClip && this.actions.bodyBorder.defaultOpacityClip.stop()
        this.actions.bodyBlur.defaultOpacityClip && this.actions.bodyBlur.defaultOpacityClip.stop()

        this.actions.icon.defaultOpacityClip && this.actions.icon.defaultOpacityClip.stop()

        this.actions.icon.panningScaleClip && this.actions.icon.panningScaleClip.play()
    }

    createWebGlAnimations = obj => {
        const { timeoutRandomizerScale } = this.props
        this.iconObj = obj.getObjectByName('icon', true).children[0]
        this.bodyBlurObj = obj.getObjectByName('bodyBlur', true).children[0]
        this.bodyBorderObj = obj.getObjectByName('bodyBorder', true).children[0]

        // https://threejs.org/docs/#api/en/animation/AnimationMixer
        // "The AnimationMixer is a player for animations on a particular object in the scene.
        //  When multiple objects in the scene are animated independently, one AnimationMixer may be used for each object."
        if (obj && this.iconObj) {
            this.mixers.bodyBorder = new THREE.AnimationMixer(this.bodyBorderObj)
            this.mixers.bodyBlur = new THREE.AnimationMixer(this.bodyBlurObj)
            this.mixers.icon = new THREE.AnimationMixer(this.iconObj)

            // SPRITE ANIMS

            // DEFAULT Animations
            // Animation for default border scaling
            var defaultScaleKF = new THREE.VectorKeyframeTrack(
                '.scale',
                Hotspot3DSpeeds.HOTSPOT_DEFAULT_BORDER_SCALE_ANIM_TIMING,
                Hotspot3DAttributeValues.HOTSPOT_DEFAULT_BORDER_SCALE_ANIM_KEYFRAME_SEQUENCE
            )
            this.clips.defaultScaleClip = new THREE.AnimationClip(
                'defaultScale',
                Hotspot3DSpeeds.HOTSPOT_DEFAULT_BORDER_OPACITY_ANIM_DURATION,
                [defaultScaleKF]
            )

            this.actions.bodyBorder.defaultScaleClip = this.mixers.bodyBorder.clipAction(this.clips.defaultScaleClip)
            this.actions.bodyBorder.defaultScaleClip.loop = THREE.LoopRepeat

            this.actions.bodyBlur.defaultScaleClip = this.mixers.bodyBlur.clipAction(this.clips.defaultScaleClip)
            this.actions.bodyBlur.defaultScaleClip.loop = THREE.LoopRepeat

            // Animation for default border opacity loop
            var defaultBorderOpacityKF = new THREE.VectorKeyframeTrack(
                '.material.opacity',
                Hotspot3DSpeeds.HOTSPOT_DEFAULT_BORDER_OPACITY_ANIM_TIMING,
                Hotspot3DAttributeValues.HOTSPOT_DEFAULT_BORDER_OPACITY_ANIM_KEYFRAME_SEQUENCE
            )
            this.clips.defaultBorderOpacityClip = new THREE.AnimationClip(
                'defaultOpacity',
                Hotspot3DSpeeds.HOTSPOT_DEFAULT_BORDER_OPACITY_ANIM_DURATION,
                [defaultBorderOpacityKF]
            )

            this.actions.bodyBorder.defaultOpacityClip = this.mixers.bodyBorder.clipAction(
                this.clips.defaultBorderOpacityClip
            )
            this.actions.bodyBorder.defaultOpacityClip.loop = THREE.LoopRepeat

            this.actions.bodyBlur.defaultOpacityClip = this.mixers.bodyBlur.clipAction(
                this.clips.defaultBorderOpacityClip
            )
            this.actions.bodyBlur.defaultOpacityClip.loop = THREE.LoopRepeat

            // Animation for default icon opacity loop
            var defaultIconOpacityKF = new THREE.VectorKeyframeTrack(
                '.material.opacity',
                Hotspot3DSpeeds.HOTSPOT_DEFAULT_ICON_OPACITY_ANIM_TIMING,
                Hotspot3DAttributeValues.HOTSPOT_DEFAULT_ICON_OPACITY_ANIM_KEYFRAME_SEQUENCE
            )
            this.clips.defaultIconOpacityClip = new THREE.AnimationClip(
                'defaultOpacity',
                Hotspot3DSpeeds.HOTSPOT_DEFAULT_ICON_OPACITY_ANIM_DURATION,
                [defaultIconOpacityKF]
            )

            this.actions.icon.defaultOpacityClip = this.mixers.icon.clipAction(this.clips.defaultIconOpacityClip)
            this.actions.icon.defaultOpacityClip.loop = THREE.LoopRepeat

            // PANNING Animations
            var panningScaleKF = new THREE.VectorKeyframeTrack(
                '.scale',
                Hotspot3DSpeeds.HOTSPOT_PANNING_ICON_SCALE_ANIM_TIMING,
                Hotspot3DAttributeValues.HOTSPOT_PANNING_ICON_SCALE_ANIM_KEYFRAME_SEQUENCE
            )

            this.clips.panningScaleClip = new THREE.AnimationClip(
                'defaultScale',
                Hotspot3DSpeeds.HOTSPOT_PANNING_ICON_SCALE_ANIM_DURATION,
                [panningScaleKF]
            )

            this.actions.icon.panningScaleClip = this.mixers.icon.clipAction(this.clips.panningScaleClip)
            this.actions.icon.panningScaleClip.loop = THREE.LoopOnce
            this.actions.icon.panningScaleClip.clampWhenFinished = true

            // VISITED Animations
            var visitedClipKF = new THREE.VectorKeyframeTrack(
                '.material.opacity',
                Hotspot3DSpeeds.HOTSPOT_VISITED_ICON_OPACITY_ANIM_TIMING,
                Hotspot3DAttributeValues.HOTSPOT_VISITED_ICON_OPACITY_ANIM_KEYFRAME_SEQUENCE
            )

            this.clips.visitedClip = new THREE.AnimationClip(
                'visitedOpacity',
                Hotspot3DSpeeds.HOTSPOT_VISITED_ICON_OPACITY_ANIM_DURATION,
                [visitedClipKF]
            )

            this.actions.icon.visitedClip = this.mixers.icon.clipAction(this.clips.visitedClip)
            this.actions.icon.visitedClip.loop = THREE.LoopOnce
            this.actions.icon.visitedClip.clampWhenFinished = true

            this.actions.bodyBlur.visitedClip = this.mixers.bodyBlur.clipAction(this.clips.visitedClip)
            this.actions.bodyBlur.visitedClip.loop = THREE.LoopOnce
            this.actions.bodyBlur.visitedClip.clampWhenFinished = true

            this.actions.bodyBorder.visitedClip = this.mixers.bodyBorder.clipAction(this.clips.visitedClip)
            this.actions.bodyBorder.visitedClip.loop = THREE.LoopOnce
            this.actions.bodyBorder.visitedClip.clampWhenFinished = true
        }

        this.playDefaultAnimations = setTimeout(() => {
            this.actions.bodyBorder.defaultScaleClip && this.actions.bodyBorder.defaultScaleClip.play()
            this.actions.bodyBlur.defaultScaleClip && this.actions.bodyBlur.defaultScaleClip.play()

            this.actions.bodyBorder.defaultOpacityClip && this.actions.bodyBorder.defaultOpacityClip.play()
            this.actions.bodyBlur.defaultOpacityClip && this.actions.bodyBlur.defaultOpacityClip.play()

            this.actions.icon.defaultOpacityClip && this.actions.icon.defaultOpacityClip.play()
        }, Math.random() * timeoutRandomizerScale)

        this.animateMixers()
    }

    handleMouseOver = e => {
        const { setCursor, useResponsiveUI, onMouseEnter, viewportWidth } = this.props
        // Use of viewport width and isFullScreenActive here to allow for Preview in highest size embed and isn't in Full screen mode
        if (useResponsiveUI && viewportWidth < Breakpoints.DisplayPreviewWidthEmbedded && !isFullScreenActive()) {
            // Callback to global cursor state
            setCursor && setCursor('pointer')
        } else {
            // Only trigger hover when above correct size when in responsive UI
            if (
                !useResponsiveUI ||
                displayFeatureBasedOnViewportSize(Capabilities.IE_HOVER_EXPAND_HOTSPOT, { viewportWidth })
            ) {
                onMouseEnter && onMouseEnter(e)
            }
        }
    }

    handleMouseExit = e => {
        const { setCursor, useResponsiveUI, viewportWidth } = this.props
        // Use of viewport width and isFullScreenActive here to allow for Preview in highest size embed and isn't in Full screen mode
        if (useResponsiveUI && viewportWidth < Breakpoints.DisplayPreviewWidthEmbedded && !isFullScreenActive()) {
            // Callback to global cursor state
            setCursor && setCursor('grab')
        }
        // Toggling the preview in and out is handled in the preview hover state, so no need to manage here
    }

    handleClick = () => {
        const { currentVideoState, rootObj } = this.state
        const { isMobile, onClick } = this.props
        // Hide positional media button while playing if on mobile
        if (isMobile) {
            // Checking for prior state, i.e., show if prior state was playing, hide if prior state was paused
            rootObj.visible = currentVideoState === 'playing'
        }

        onClick && onClick()
    }

    animateMixers = () => {
        if (!this.allowAnimations) return
        var delta = this.clock.getDelta()
        this.mixers &&
            Object.keys(this.mixers).forEach(mixerKey => {
                this.mixers[mixerKey].update(delta)
            })
        this.raf = requestAnimationFrame(this.animateMixers)
    }

    componentWillUnmount() {
        this.allowAnimations = false
        const { rootObj } = this.state

        disposeAnimations({
            mixers: this.mixers,
            actions: this.actions,
            clips: this.clips,
        })
        dispose3DObject(rootObj)
        this.raf && cancelAnimationFrame(this.raf)
        this.rafMesh && cancelAnimationFrame(this.rafMesh)
        clearTimeout(this.createAnimations)
        clearTimeout(this.defaultAnimations)
        clearTimeout(this.playDefaultAnimations)
        this.raf = null
        this.rafMesh = null
        this.clock = null
        this.setState({ rootObj: null })
    }

    animateHotspot = () => {
        if (!this.allowAnimations) return
        // NOTE: EXPERIMENT WITH APPLYING QUATERNIONS HERE TO MINIMIZE DISTORTION WHEN ROTATING CAMERA
        const { camera } = this.props
        const { rootObj } = this.state
        rootObj && rootObj.quaternion.copy(camera.quaternion)
        this.rafMesh = requestAnimationFrame(this.animateHotspot)
    }

    render() {
        const { rootObj } = this.state
        const {
            position,
            isVisible,
            onMouseEnter,
            onClick,
            type,
            meshPosition,
            currentState,
            isMobile,
            useResponsiveUI,
            ...props
        } = this.props

        let iconResource = type === 'text' ? HOTSPOT_TEXT_ATLAS : HOTSPOT_ICONS
        let numberOfState = type === 'text' ? 3 : 6
        let iconDescriptor = {
            main: {},
            blur: {},
        }

        switch (type) {
            case MEDIA_TYPES.photo:
                iconDescriptor.blur.persistent = 0
                iconDescriptor.main.persistent = 1
                break
            case MEDIA_TYPES.panorama:
                iconDescriptor.blur.persistent = 4
                iconDescriptor.main.persistent = 5
                break
            case MEDIA_TYPES.video:
                iconDescriptor.blur.persistent = 2
                iconDescriptor.main.persistent = 3
                break
            case 'text':
                iconDescriptor.blur.persistent = 0
                iconDescriptor.main.persistent = 1
                break
            default:
                iconDescriptor.main.persistent = 0
                iconDescriptor.blur.persistent = 1
        }
        return (
            rootObj && (
                <Fragment>
                    <HitCircle3D
                        opacity={0}
                        rootObj={rootObj}
                        // renderOrder={8}
                        handleTouchStart={onClick}
                        handleTouchEnd={onClick}
                        handleClick={useResponsiveUI ? onClick : null}
                        handleMouseOver={!isMobile ? this.handleMouseOver : null}
                        handleMouseOut={!isMobile ? this.handleMouseExit : null}
                        isMobile={isMobile}
                        // Needed to avoid z-fighting in certain cases
                        depthOffset={-0.1}
                        radius={props.hitRadius + Hotspot3DAttributeValues.HIT_MESH_OFFSET}
                        meshPosition={meshPosition}
                        position={meshPosition}
                        camera={props.camera}
                        renderer={props.renderer}
                        scene={props.scene}
                        enableGaze={enableGaze}
                    />
                    <Fragment>
                        <SpriteSheet3D
                            depthOffset={0}
                            spriteName={'bodyBlur'}
                            renderOrder={1}
                            spriteResource={HOTSPOT_BODY}
                            spriteScale={Hotspot3DAttributeValues.BODY_SCALE}
                            numberOfStates={3}
                            currentState={'persistent'}
                            rootObj={rootObj}
                            stateIndexMap={{
                                persistent: 1,
                            }}
                        />
                        <SpriteSheet3D
                            depthOffset={0}
                            spriteName={'bodyFill'}
                            renderOrder={2}
                            spriteResource={HOTSPOT_BODY}
                            spriteScale={Hotspot3DAttributeValues.BODY_SCALE}
                            numberOfStates={3}
                            alwaysRender={true}
                            currentState={'persistent'}
                            rootObj={rootObj}
                            stateIndexMap={{
                                persistent: 2,
                            }}
                        />
                        <SpriteSheet3D
                            depthOffset={0}
                            renderOrder={3}
                            spriteName={'bodyBorder'}
                            spriteResource={HOTSPOT_BODY}
                            spriteScale={Hotspot3DAttributeValues.BODY_SCALE}
                            numberOfStates={3}
                            currentState={'persistent'}
                            rootObj={rootObj}
                            stateIndexMap={{
                                persistent: 0,
                            }}
                        />
                    </Fragment>
                    <Fragment>
                        <SpriteSheet3D
                            depthOffset={0}
                            renderOrder={5}
                            spriteName={'icon'}
                            spriteResource={iconResource}
                            numberOfStates={numberOfState}
                            currentState={'persistent'}
                            spriteScale={Hotspot3DAttributeValues.ICON_SCALE}
                            rootObj={rootObj}
                            stateIndexMap={iconDescriptor.main}
                        />
                    </Fragment>
                    {currentState === 'visited' && (
                        <Fragment>
                            <SpriteSheet3D
                                depthOffset={0}
                                renderOrder={5}
                                spriteName={'icon'}
                                spriteResource={VISITED_CHECK}
                                numberOfStates={3}
                                currentState={'persistent'}
                                rootObj={rootObj}
                                stateIndexMap={{
                                    persistent: 2,
                                }}
                                spriteScale={Hotspot3DAttributeValues.VISITED_CHECK_SCALE}
                                meshPosition={Hotspot3DAttributeValues.VISITED_CHECK_OFFSET}
                            />
                            <SpriteSheet3D
                                depthOffset={0}
                                renderOrder={6}
                                spriteName={'iconBlur'}
                                spriteResource={VISITED_CHECK}
                                numberOfStates={3}
                                currentState={'persistent'}
                                rootObj={rootObj}
                                stateIndexMap={{
                                    persistent: 1,
                                }}
                                spriteScale={Hotspot3DAttributeValues.VISITED_CHECK_SCALE}
                                meshPosition={Hotspot3DAttributeValues.VISITED_CHECK_OFFSET}
                            />
                            <SpriteSheet3D
                                depthOffset={0}
                                renderOrder={7}
                                spriteName={'icon'}
                                spriteResource={VISITED_CHECK}
                                numberOfStates={3}
                                currentState={'persistent'}
                                rootObj={rootObj}
                                stateIndexMap={{
                                    persistent: 0,
                                }}
                                spriteScale={Hotspot3DAttributeValues.VISITED_CHECK_SCALE}
                                meshPosition={Hotspot3DAttributeValues.VISITED_CHECK_OFFSET}
                            />
                        </Fragment>
                    )}
                </Fragment>
            )
        )
    }
}

Hotspot3D.defaultProps = {
    meshPosition: Hotspot3DAttributeValues.MESH_POSITION,
    hitRadius: Hotspot3DAttributeValues.HIT_RADIUS,
    height: Hotspot3DAttributeValues.HEIGHT,
    absoluteScale: Hotspot3DAttributeValues.ABSOLUTE_SCALE,
    timeoutRandomizerScale: Hotspot3DSpeeds.HOTSPOT_TIMEOUT_RANDOMIZER_SCALE,
}

export default Hotspot3D
